import { useReadOnly } from "../../hooks/useReadOnly";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";
import {
  IoAlertCircleOutline,
  IoDownloadOutline,
} from "react-icons/io5";
import { userMapsApi } from "../../services/userMapsApi";
import { areaShapeLoader } from "../../geo/areaShapes";
import type { AreaCell } from "../../geo/areaShapes";
import { areaCountry } from "../../config/countries";
import { realtimeSupports } from "../../config/products";
import { projectsApi, Project } from "../../services/projectsApi";
import ProjectEditModal from "./ProjectEditModal";
import AreaThumbnail from "./AreaThumbnail";
import AreaCard, { AreaIdBadges } from "./AreaCard";
import AreaGroups from "./AreaGroups";
import NewAreaMenu from "./NewAreaMenu";
import Spinner from "../common/Spinner";
import {
  shapesToFeatureCollection,
  downloadJson,
  safeFilename,
} from "../../utils/geo";
import { areaEditPath } from "../../utils/areaEditPath";
import { correctAreaType } from "../../utils/areaLookup";

const EditIcon = MdModeEditOutline as any;
const DeleteIcon = MdDeleteOutline as any;
const AlertIcon = IoAlertCircleOutline as any;
const DownloadIcon = IoDownloadOutline as any;

interface SavedArea {
  id: string;
  name: string;
  description: string;
  type: string;
  ids?: string[];
  geometry?: string;
  country_code?: string | null;
}

const SavedAreas: React.FC = () => {
  const navigate = useNavigate();
  // Read-only (support view or a viewer): no creating, editing or deleting.
  const { readOnly } = useReadOnly();
  const [savedAreas, setSavedAreas] = useState<SavedArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState<SavedArea | null>(null);
  const [loadingEdit, setLoadingEdit] = useState<string | null>(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState<string | null>(null);
  // Projects — named groups of saved areas (localStorage; see projectsApi
  // and docs/projects-persistence.md).
  const [projects, setProjects] = useState<Project[]>([]);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [creatingProject, setCreatingProject] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [deletingProject, setDeletingProject] = useState(false);

  useEffect(() => {
    const loadSavedAreas = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await userMapsApi.loadGeometries();
        const sortedAreas = [...response.maps].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setSavedAreas(sortedAreas);
      } catch (err) {
        console.error("Failed to load saved areas:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load saved areas"
        );
      } finally {
        setLoading(false);
      }
    };

    loadSavedAreas();
  }, []);

  const refreshProjects = useCallback(async () => {
    try {
      setProjects(await projectsApi.list());
    } catch (err) {
      console.warn("Could not load projects:", err);
    }
  }, []);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  const areaNameById = useMemo(
    () => Object.fromEntries(savedAreas.map((a) => [a.id, a.name])),
    [savedAreas]
  );

  // Membership picker is scoped to the areas Real Time (and projects today) can
  // use: custom polygons in a country with Real Time, or saved before areas had one.
  const customPolygonAreas = useMemo(
    () => savedAreas.filter(realtimeSupports),
    [savedAreas]
  );
  const openEditProject = (p: Project) => {
    setCreatingProject(false);
    setEditingProject(p);
  };

  const closeProjectModal = () => {
    setEditingProject(null);
    setCreatingProject(false);
  };

  const openCreateProject = () => {
    setEditingProject(null);
    setCreatingProject(true);
  };

  const saveProject = async (name: string, areaIds: string[]) => {
    if (!name) return;
    try {
      setSavingEdit(true);
      if (editingProject) {
        await projectsApi.update(editingProject.id, { name, areaIds });
      } else {
        await projectsApi.create({ name, areaIds });
      }
      closeProjectModal();
      await refreshProjects();
    } catch (err) {
      console.error("Failed to save project:", err);
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;
    try {
      setDeletingProject(true);
      await projectsApi.remove(projectToDelete.id);
      setProjectToDelete(null);
      await refreshProjects();
    } catch (err) {
      console.error("Failed to delete project:", err);
    } finally {
      setDeletingProject(false);
    }
  };

  const handleDelete = (area: SavedArea) => {
    setAreaToDelete(area);
    setIsDeleteModalOpen(true);
  };

  const handleDownload = async (area: SavedArea) => {
    try {
      setLoadingDownload(area.id);

      let cells: AreaCell[];

      if (area.type === "custom_polygon" && area.geometry) {
        cells = await areaShapeLoader.load(area);
      } else if (area.ids && area.ids.length > 0) {
        cells = await loadGeos(area, area.ids);
      } else {
        throw new Error("No geometry data available for this area");
      }

      const fc = shapesToFeatureCollection(
        cells.map((c) => ({
          id: c.geo_id,
          geometry: c.geometry,
          properties: {
            id: c.geo_id,
            area_name: area.name,
            area_type: area.type,
            country: areaCountry(area),
          },
        }))
      );

      if (fc.features.length === 0) {
        throw new Error("Could not parse any geometries for this area");
      }

      downloadJson(fc, `telcofy-${safeFilename(area.name)}`);
    } catch (error) {
      console.error("Failed to download area:", error);
      alert(
        `Failed to download area: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setLoadingDownload(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!areaToDelete) return;

    try {
      setLoadingDelete(true);
      await userMapsApi.deleteGeometries(areaToDelete.id);
      setSavedAreas((prevAreas) =>
        prevAreas.filter((a) => a.id !== areaToDelete.id)
      );
      setIsDeleteModalOpen(false);
      setAreaToDelete(null);
    } catch (error) {
      console.error("Failed to delete area:", error);
      alert(
        `Failed to delete area: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setAreaToDelete(null);
  };

  // The shapes of a saved area's ids, from the maps API. They need the area's country.
  const loadGeos = (area: SavedArea, ids: string[]) =>
    areaShapeLoader.load({ id: area.id, type: area.type, ids, country_code: area.country_code });

  const handleEdit = async (area: SavedArea) => {
    try {
      const path = areaEditPath(area.type);

      if (path === "/custom-polygon") {
        if (!area.geometry) {
          throw new Error("No geometry data available for editing this area");
        }
        navigate(path, {
          state: {
            editMode: true,
            savedArea: area,
            wktGeometry: area.geometry,
          },
        });
      } else if (area.ids) {
        setLoadingEdit(area.id);
        const geoData = await loadGeos(area, area.ids);
        const correctedArea = { ...area, geo_type: correctAreaType(area) };
        navigate(path, {
          state: {
            editMode: true,
            savedArea: correctedArea,
            geometries: geoData,
          },
        });
      } else {
        throw new Error(
          path === "/road-links"
            ? "No road IDs available for editing this area"
            : "No geometry data available for editing this area"
        );
      }
    } catch (error) {
      console.error("Failed to edit area:", error);
      alert(
        `Failed to edit area: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setLoadingEdit(null);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-ink-faint mb-3">
            Library
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-ink mb-3">
            Areas
          </h1>
          <p className="text-lg text-ink-muted">
            Your projects and saved areas, and a way to create new ones.
          </p>
        </div>
        {!readOnly && <NewAreaMenu />}
      </div>

      {/* Projects — named groups of saved areas */}
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xl">📁</span>
          <h2 className="font-display text-2xl font-bold text-ink">Projects</h2>
          <span className="inline-flex items-center rounded-full bg-paper-200 px-2.5 py-1 text-xs font-mono text-ink-subtle">
            {projects.length}
          </span>
          {!readOnly && (
            <button
              onClick={openCreateProject}
              className="ml-auto inline-flex items-center gap-1 rounded-full border border-paper-300 px-3 py-1.5 text-sm font-medium text-ink-muted transition-colors hover:bg-paper-100 hover:text-ink"
            >
              + New project
            </button>
          )}
        </div>
        {projects.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-paper-300 bg-surface px-5 py-8 text-center">
            <p className="text-sm text-ink-muted">No projects yet.</p>
            <p className="mt-1 text-xs text-ink-faint">
              Use “New project” above, or select areas in Monitor Areas and choose “Save as project”.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => {
              const memberNames = p.areaIds.map((id) => areaNameById[id] || id);
              return (
                <div
                  key={p.id}
                  className="group relative overflow-hidden rounded-2xl border border-paper-300/70 bg-surface p-4 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-card hover:border-brand-500/30"
                >
                  <div className="-mx-4 -mt-4 mb-3">
                    <AreaThumbnail
                      areas={savedAreas.filter((a) => p.areaIds.includes(a.id))}
                      label={`Outline of ${p.name}`}
                    />
                  </div>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="font-display text-base font-semibold text-ink truncate">
                      {p.name}
                    </h4>
                    <div className="flex gap-1 flex-shrink-0">
                      {!readOnly && (
                        <button
                          onClick={() => openEditProject(p)}
                          title="Edit project"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-brand-50 hover:text-brand-600 transition-colors"
                        >
                          <EditIcon className="h-4 w-4" />
                        </button>
                      )}
                      {!readOnly && (
                        <button
                          onClick={() => setProjectToDelete(p)}
                          title="Delete project"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-bad-soft hover:text-bad-ink transition-colors"
                        >
                          <DeleteIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-ink-faint mb-3">
                    {p.areaIds.length} area{p.areaIds.length === 1 ? "" : "s"}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {memberNames.slice(0, 3).map((n, i) => (
                      <span
                        key={i}
                        className="inline-flex max-w-[130px] items-center truncate rounded-full border border-paper-300 px-2 py-0.5 text-[10px] font-mono text-ink-subtle"
                      >
                        {n}
                      </span>
                    ))}
                    {memberNames.length > 3 && (
                      <span className="inline-flex items-center rounded-full bg-paper-200 px-2 py-0.5 text-[10px] font-mono text-ink-subtle">
                        +{memberNames.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-20">
          <Spinner size="lg" label="Loading saved areas…" />
        </div>
      ) : error ? (
        <div className="flex items-start gap-3 rounded-2xl bg-bad-soft border border-bad-line p-4 text-bad-ink">
          <AlertIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span className="text-sm">{error}</span>
        </div>
      ) : savedAreas.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-paper-300 bg-surface py-16 text-center">
          <p className="text-base text-ink-muted">No saved areas found.</p>
          <p className="mt-1 text-sm text-ink-faint">
            Create and save some geometry selections to see them here.
          </p>
        </div>
      ) : (
        <AreaGroups
          areas={savedAreas}
          defaultCollapsed
          renderCard={(area) => (
            <AreaCard
              area={area}
              actions={
                <>
                  <button
                    onClick={() => handleDownload(area)}
                    disabled={loadingDownload === area.id}
                    title="Download as GeoJSON"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-brand-50 hover:text-brand-600 transition-colors disabled:opacity-50"
                  >
                    {loadingDownload === area.id ? (
                      <div className="tf-spinner h-4 w-4 border-2" />
                    ) : (
                      <DownloadIcon className="h-4 w-4" />
                    )}
                  </button>
                  {!readOnly && (
                    <button
                      onClick={() => handleEdit(area)}
                      disabled={loadingEdit === area.id}
                      title="Edit area"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-brand-50 hover:text-brand-600 transition-colors disabled:opacity-50"
                    >
                      {loadingEdit === area.id ? (
                        <div className="tf-spinner h-4 w-4 border-2" />
                      ) : (
                        <EditIcon className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  {!readOnly && (
                    <button
                      onClick={() => handleDelete(area)}
                      disabled={loadingDelete && areaToDelete?.id === area.id}
                      title="Delete area"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-bad-soft hover:text-bad-ink transition-colors disabled:opacity-50"
                    >
                      {loadingDelete && areaToDelete?.id === area.id ? (
                        <div className="tf-spinner h-4 w-4 border-2" />
                      ) : (
                        <DeleteIcon className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </>
              }
              footer={
                <>
                  <p className="text-xs text-ink-faint mb-3">
                    {area.ids ? `${area.ids.length} geometries selected` : "Custom polygon"}
                  </p>
                  <AreaIdBadges area={area} />
                </>
              }
            />
          )}
        />
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40 p-4 animate-fade-in"
          onClick={handleCancelDelete}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-surface p-6 shadow-card-hover"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-xl font-bold text-ink mb-2">
              Delete saved area
            </h3>
            <p className="text-sm text-ink-muted mb-6">
              Are you sure you want to delete{" "}
              <span className="font-medium text-ink">
                {areaToDelete?.name}
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelDelete}
                disabled={loadingDelete}
                className="inline-flex items-center rounded-full border border-paper-300 px-4 py-2 text-sm font-medium text-ink-muted hover:bg-paper-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={loadingDelete}
                className="inline-flex items-center gap-2 rounded-full bg-bad px-4 py-2 text-sm font-semibold text-on-brand hover:bg-bad/90 transition-colors disabled:opacity-50"
              >
                {loadingDelete && <div className="tf-spinner h-4 w-4 border-2 border-on-brand/30 border-t-on-brand" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ProjectEditModal
        open={!!(editingProject || creatingProject)}
        project={editingProject}
        areas={customPolygonAreas}
        saving={savingEdit}
        onClose={closeProjectModal}
        onSave={saveProject}
      />

      {/* Delete project modal */}
      {projectToDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40 p-4 animate-fade-in"
          onClick={() => setProjectToDelete(null)}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-surface p-6 shadow-card-hover"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-xl font-bold text-ink mb-2">
              Delete project
            </h3>
            <p className="text-sm text-ink-muted mb-6">
              Delete{" "}
              <span className="font-medium text-ink">{projectToDelete.name}</span>?
              The areas themselves are not removed.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setProjectToDelete(null)}
                disabled={deletingProject}
                className="inline-flex items-center rounded-full border border-paper-300 px-4 py-2 text-sm font-medium text-ink-muted hover:bg-paper-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteProject}
                disabled={deletingProject}
                className="inline-flex items-center gap-2 rounded-full bg-bad px-4 py-2 text-sm font-semibold text-on-brand hover:bg-bad/90 transition-colors disabled:opacity-50"
              >
                {deletingProject && (
                  <div className="tf-spinner h-4 w-4 border-2 border-on-brand/30 border-t-on-brand" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedAreas;
